import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  DocumentTextIcon,
  FolderIcon,
  PlusIcon,
  SparklesIcon,
} from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/catalyst/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useMatter } from '@/hooks/useMatter';
import { cn } from '@/lib/utils';
import {
  useCorrespondencePackageResetComponentsMutation,
  useCorrespondencePackageRetrieveQuery,
} from '@/services/api/correspondenceService';
import type { CorrespondenceComponentSchema } from '@/services/api/generated/correspondenceService';
import type { Blob as BlobType } from '@/services/types/blob-types';

import { useCorrespondenceUI } from '../contexts/CorrespondenceUIContext';
import { TypeBadge } from '../header/PackageTitle';
import { usePackageComponents, usePackageValidation } from '../hooks';
import type { CorrespondencePackageType } from '../types';
import { ComponentItem } from './ComponentItem';
import { DocumentSelector } from './DocumentSelector';
import { TemplateSearchList } from './TemplateSearchList';

interface PackageComponentsListProps {
  showAddPanel: boolean;
  onShowAddPanel: (show: boolean) => void;
}

/**
 * Component list for managing correspondence package components
 * Provides drag-and-drop reordering and component management
 *
 * Uses RTKQ hooks directly for granular subscriptions.
 */
export const PackageComponentsList = ({
  showAddPanel,
  onShowAddPanel,
}: PackageComponentsListProps) => {
  const { matterId } = useMatter();
  const ui = useCorrespondenceUI();
  const {
    packageId,
    packageMode,
    selectedComponentId,
    setSelectedComponentId,
  } = ui;

  const {
    components,
    removeComponent,
    reorderComponents,
    isMutating: isComponentMutating,
  } = usePackageComponents({
    packageId,
    skip: !packageId,
  });

  const {
    validationStatus,
    matterContext,
    isFetching: isLoadingValidation,
  } = usePackageValidation({
    packageId,
    skip: !packageId,
  });

  // Get package-level status for component inheritance and mutability
  const { packageStatus, packageType, isMutable, renderedPdfUrl } =
    useCorrespondencePackageRetrieveQuery(
      { matterId, id: packageId || '' },
      {
        skip: !packageId,
        selectFromResult: ({ data }) => ({
          packageStatus: data?.status,
          packageType: data?.package_type as
            | CorrespondencePackageType
            | undefined,
          isMutable: data?.is_mutable ?? true,
          renderedPdfUrl: data?.rendered_pdf_url,
        }),
      },
    );

  const [resetComponents, { isLoading: isResetting }] =
    useCorrespondencePackageResetComponentsMutation();

  // Reset confirmation dialog state
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Local state for optimistic DnD updates
  const [localComponents, setLocalComponents] = useState<
    CorrespondenceComponentSchema[]
  >(components || []);

  // Sync local state when RTK Query cache updates
  useEffect(() => {
    setLocalComponents(components || []);
  }, [components]);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const isEditMode = packageMode === 'edit' && isMutable;
  const showRenderedPdfFallback =
    localComponents.length === 0 && Boolean(renderedPdfUrl) && !isMutable;
  const visibleItemCount = showRenderedPdfFallback ? 1 : localComponents.length;

  const handleResetComponents = async () => {
    if (!packageId) return;
    try {
      await resetComponents({ matterId, id: packageId }).unwrap();
      setShowResetConfirm(false);
      toast.success('Components reset to defaults');
    } catch {
      toast.error('Failed to reset components');
    }
  };

  const handleRemoveComponent = async (componentId: string) => {
    try {
      await removeComponent(componentId);
      if (componentId === selectedComponentId) {
        setSelectedComponentId(null);
      }
      toast.success('Component removed');
    } catch {
      toast.error('Failed to remove component');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localComponents.findIndex((c) => c.id === active.id);
    const newIndex = localComponents.findIndex((c) => c.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistic update: reorder immediately in local state
    const reordered = [...localComponents];
    const [movedItem] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, movedItem);
    setLocalComponents(reordered);

    try {
      await reorderComponents(reordered.map((c) => c.id));
    } catch {
      // Rollback on error
      setLocalComponents(components || []);
      toast.error('Failed to reorder components');
    }
  };

  return (
    <div className="h-full flex flex-col p-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 px-0.5">
        <div className="flex items-center gap-1">
          <h3 className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">
            Contents
          </h3>
          <span className="text-[10px] font-semibold text-zinc-400 tabular-nums">
            {visibleItemCount}
          </span>
        </div>
        {isEditMode && packageType && packageType !== 'custom' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setShowResetConfirm(true)}
                disabled={isResetting}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon
                  className={cn('size-3', isResetting && 'animate-spin')}
                />
                Reset
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Replace with standard documents for this package type
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Component List or Add Panel */}
      <div className="flex-1 min-h-0 flex flex-col">
        {showAddPanel ? (
          <div className="animate-in fade-in slide-in-from-right-3 duration-200 flex-1 flex flex-col min-h-0">
            <AddComponentPlaceholder
              onAdd={() => onShowAddPanel(false)}
              onClose={() => onShowAddPanel(false)}
              disabled={!isMutable}
            />
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            {localComponents.length > 0 ? (
              <div className="flex-1 overflow-y-auto pb-2">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={localComponents.map((c) => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-1">
                      {localComponents.map((component) => {
                        const componentValidation =
                          validationStatus.componentResults.find(
                            (r) => r.componentId === component.id,
                          );
                        return (
                          <ComponentItem
                            key={component.id}
                            component={component}
                            matterId={matterId}
                            matterContext={matterContext}
                            validationResult={componentValidation}
                            isLoadingValidation={isLoadingValidation}
                            isSelected={component.id === selectedComponentId}
                            onSelect={() =>
                              setSelectedComponentId(
                                component.id === selectedComponentId
                                  ? null
                                  : component.id,
                              )
                            }
                            onRemove={() => handleRemoveComponent(component.id)}
                            disabled={isComponentMutating}
                            packageMode={isMutable ? packageMode : 'view'}
                            packageStatus={packageStatus}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>

                {isEditMode && (
                  <button
                    type="button"
                    onClick={() => onShowAddPanel(true)}
                    className="w-full h-6 flex items-center justify-center gap-1 mt-1.5 rounded border border-dashed border-zinc-200 text-zinc-400 hover:text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50/50 transition-all"
                  >
                    <PlusIcon className="size-3" />
                    <span className="text-[10px] font-medium">Add</span>
                  </button>
                )}
              </div>
            ) : showRenderedPdfFallback ? (
              <div className="flex flex-col gap-2 py-2">
                <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <DocumentTextIcon className="h-4 w-4 text-zinc-500" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-zinc-800">Final PDF</p>
                      <p className="text-[11px] text-zinc-500">
                        Sent package preview
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <EmptyState
                onAddClick={() => onShowAddPanel(true)}
                showAddButton={isEditMode}
              />
            )}
          </div>
        )}
      </div>

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base">
              Reset Documents?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-zinc-500">
              This will replace all current documents with the default set for
              {packageType && (
                <span className="inline-flex items-center mx-1">
                  <TypeBadge packageType={packageType} />
                </span>
              )}
              packages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetComponents}
              disabled={isResetting}
              className="bg-indigo-600 hover:bg-indigo-700 text-sm"
            >
              {isResetting ? 'Resetting...' : 'Reset'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/**
 * Empty state component - minimal and elegant
 */
interface EmptyStateProps {
  onAddClick: () => void;
  showAddButton: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  onAddClick,
  showAddButton,
}) => {
  if (!showAddButton) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-center h-full">
        <DocumentTextIcon className="w-4 h-4 text-zinc-300 mb-1.5" />
        <p className="text-[11px] text-zinc-500">No documents</p>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onAddClick}
      className="w-full flex-1 flex flex-col items-center justify-center py-6 px-2 rounded border-2 border-dashed border-zinc-100 hover:border-indigo-200 hover:bg-indigo-50/10 transition-all group text-center"
    >
      <PlusIcon className="w-4 h-4 text-zinc-300 group-hover:text-indigo-500 transition-colors mb-1.5" />
      <p className="text-[11px] font-semibold text-zinc-700 group-hover:text-indigo-900 transition-colors">
        Add documents
      </p>
    </button>
  );
};

/**
 * Component type selection card - Clean minimal style
 */
interface ComponentTypeCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

const ComponentTypeCard: React.FC<ComponentTypeCardProps> = ({
  icon: Icon,
  title,
  description,
  onClick,
  disabled,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'w-full px-2 py-1.5 rounded text-left transition-all group',
      'bg-zinc-50 hover:bg-indigo-50 active:bg-indigo-100',
      disabled && 'opacity-50 cursor-not-allowed',
    )}
    disabled={disabled}
  >
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-zinc-400 group-hover:text-indigo-600 transition-colors shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-[11px] text-zinc-800 group-hover:text-indigo-900 transition-colors leading-tight">
          {title}
        </p>
        <p className="text-[10px] text-zinc-500 leading-tight">{description}</p>
      </div>
      <ChevronLeftIcon className="h-3 w-3 text-zinc-300 rotate-180 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </div>
  </button>
);

/**
 * Inline add component UI
 */
interface AddComponentPlaceholderProps {
  onAdd: () => void;
  onClose: () => void;
  disabled?: boolean;
}

type AddPanelType = 'template' | 'document' | 'mergeDocument' | null;

const AddComponentPlaceholder: React.FC<AddComponentPlaceholderProps> = ({
  onAdd,
  onClose,
  disabled = false,
}) => {
  const ui = useCorrespondenceUI();
  const { matterId, packageId } = ui;

  const {
    addTemplate,
    addBlob,
    addMergeDocument,
    isMutating: isComponentMutating,
  } = usePackageComponents({
    packageId,
    skip: !packageId,
  });

  const isDisabled = disabled || isComponentMutating;

  const [selectedType, setSelectedType] = useState<AddPanelType>(null);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);

  // Merge document state (signed auth flow)
  const [mergeSourceBlobId, setMergeSourceBlobId] = useState<string | null>(
    null,
  );
  const [mergeTemplateId, setMergeTemplateId] = useState<string | null>(null);

  const resetAndClose = () => {
    setSelectedType(null);
    setSelectedDocumentIds([]);
    setMergeSourceBlobId(null);
    setMergeTemplateId(null);
    onAdd();
  };

  const handleAddTemplate = async (templateId: string) => {
    try {
      await addTemplate(templateId);
      toast.success('Template added');
      resetAndClose();
    } catch {
      toast.error('Failed to add template');
    }
  };

  const handleAddDocuments = async () => {
    if (selectedDocumentIds.length === 0) return;
    try {
      await Promise.all(selectedDocumentIds.map((blobId) => addBlob(blobId)));
      const count = selectedDocumentIds.length;
      toast.success(count > 1 ? `${count} documents added` : 'Document added');
      resetAndClose();
    } catch {
      toast.error('Failed to add documents');
    }
  };

  const handleMergeDocumentConfirm = async () => {
    if (!mergeSourceBlobId || !mergeTemplateId) {
      toast.error('Select both a source PDF and a template');
      return;
    }
    try {
      await addMergeDocument(mergeTemplateId, mergeSourceBlobId);
      toast.success('Merge document added');
      resetAndClose();
    } catch {
      toast.error('Failed to add merge document');
    }
  };

  // PDF-only filter for merge source
  const pdfOnlyFilter = (blob: BlobType) => {
    const filename = (blob.original_filename || blob.name || '').toLowerCase();
    return filename.endsWith('.pdf');
  };

  const handleBack = () => {
    if (selectedType) {
      setSelectedType(null);
      setSelectedDocumentIds([]);
      setMergeSourceBlobId(null);
      setMergeTemplateId(null);
    } else {
      onClose();
    }
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <button
        type="button"
        onClick={handleBack}
        className="flex items-center gap-1 text-[11px] font-semibold text-zinc-500 hover:text-zinc-800 mb-2 px-1 py-0.5 -ml-1 rounded hover:bg-zinc-100 transition-colors w-fit"
      >
        <ChevronLeftIcon className="h-3 w-3" />
        <span>Back</span>
      </button>

      <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
        {/* Transmitted package notice */}
        {disabled && (
          <div className="mb-2 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded">
            <p className="text-[10px] text-amber-700 font-medium leading-tight">
              This package has been transmitted and cannot be modified.
            </p>
          </div>
        )}

        {!selectedType ? (
                <div className="space-y-1 animate-in fade-in slide-in-from-bottom-2 duration-200">
            <ComponentTypeCard
              icon={DocumentTextIcon}
              title="Document Template"
              description="Auto-filled merge fields"
              onClick={() => setSelectedType('template')}
              disabled={isDisabled}
            />
            <ComponentTypeCard
              icon={FolderIcon}
              title="Matter Documents"
              description="Select from case files"
              onClick={() => setSelectedType('document')}
              disabled={isDisabled}
            />
            <ComponentTypeCard
              icon={SparklesIcon}
              title="Signed Document Merge"
              description="Merge signed PDF with template"
              onClick={() => setSelectedType('mergeDocument')}
              disabled={isDisabled}
            />
          </div>
        ) : (
          <div className="h-full animate-in fade-in slide-in-from-right-2 duration-200 flex flex-col min-h-0">
            {selectedType === 'template' && (
              <TemplateSearchList
                matterId={matterId}
                onSelectTemplate={handleAddTemplate}
                isLoading={isDisabled}
                includeTemplateTypes={['DOCX', 'HTML']}
                enablePreview
                confirmButtonLabel="Add Template"
              />
            )}
            {selectedType === 'document' && matterId && (
              <DocumentSelector
                matterId={matterId}
                selectedBlobIds={selectedDocumentIds}
                onSelectionChange={setSelectedDocumentIds}
                onConfirm={handleAddDocuments}
                multiSelect
                enablePreview
                enableUpload
                acceptFileTypes=".pdf,.jpg,.jpeg,.png"
                previewDialogTitle="Matter Document Preview"
              />
            )}
            {selectedType === 'mergeDocument' && matterId && (
              <MergeDocumentPanel
                matterId={matterId}
                sourceBlobId={mergeSourceBlobId}
                onSourceBlobChange={setMergeSourceBlobId}
                templateId={mergeTemplateId}
                onTemplateChange={setMergeTemplateId}
                onConfirm={handleMergeDocumentConfirm}
                isLoading={isDisabled}
                filterBlobs={pdfOnlyFilter}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Merge document panel - select source PDF and template to merge
 * Follows the same layout pattern as treatment/v3 signed authorization flow
 */
interface MergeDocumentPanelProps {
  matterId: string;
  sourceBlobId: string | null;
  onSourceBlobChange: (blobId: string | null) => void;
  templateId: string | null;
  onTemplateChange: (templateId: string | null) => void;
  onConfirm: () => void;
  isLoading: boolean;
  filterBlobs?: (blob: BlobType) => boolean;
}

const MergeDocumentPanel: React.FC<MergeDocumentPanelProps> = ({
  matterId,
  sourceBlobId,
  onSourceBlobChange,
  templateId,
  onTemplateChange,
  onConfirm,
  isLoading,
  filterBlobs,
}) => {
  const selectedBlobIds = sourceBlobId ? [sourceBlobId] : [];

  return (
    <div className="space-y-6">
      {/* Step 1: Select source PDF */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold shrink-0">
            1
          </span>
          <h4 className="text-sm font-semibold text-zinc-700">
            Choose Signed Authorization PDF
          </h4>
          {sourceBlobId && (
            <CheckCircleIcon className="w-4 h-4 text-emerald-500 ml-auto shrink-0" />
          )}
        </div>
        <p className="text-xs text-zinc-500 ml-7">
          Select the signed authorization from matter documents (PDF only)
        </p>
        <div className="ml-7">
          <DocumentSelector
            matterId={matterId}
            selectedBlobIds={selectedBlobIds}
            onSelectionChange={(ids) => onSourceBlobChange(ids[0] || null)}
            onConfirm={() => {}}
            filterBlobs={filterBlobs}
            multiSelect={false}
            showConfirmButton={false}
            enablePreview
            enableUpload
            acceptFileTypes=".pdf"
            previewDialogTitle="Signed Authorization Preview"
          />
        </div>
      </div>

      {/* Step 2: Select template */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold shrink-0">
            2
          </span>
          <h4 className="text-sm font-semibold text-zinc-700">
            Choose Authorization Template (PDF)
          </h4>
          {templateId && (
            <CheckCircleIcon className="w-4 h-4 text-emerald-500 ml-auto shrink-0" />
          )}
        </div>
        <p className="text-xs text-zinc-500 ml-7">
          Pick the firm authorization template that will merge with the signed
          PDF
        </p>
        <div className="ml-7">
          <TemplateSearchList
            matterId={matterId}
            onSelectTemplate={(id) => {
              onTemplateChange(id);
              toast.success('Authorization template selected');
            }}
            isLoading={isLoading}
            includeTemplateTypes={['PDF']}
            enablePreview
            autoConfirmOnSelect
          />
        </div>
      </div>

      {/* Confirm button - sticky footer */}
      <div className="sticky bottom-0 pt-4 border-t border-zinc-200 bg-white">
        <Button
          onClick={onConfirm}
          color="indigo"
          className="w-full shadow-sm"
          disabled={isLoading || !sourceBlobId || !templateId}
        >
          Add Merge Document
        </Button>
        <p className="mt-2 text-xs text-zinc-500 text-center">
          The PDF will be merged with the template on render
        </p>
      </div>
    </div>
  );
};
